import React, {useEffect, useState} from "react";
import {Link, useHistory, useLocation} from "react-router-dom";
import Typography from "@material-ui/core/Typography";
import { TextField } from "formik-material-ui";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import { Formik, Form, Field, FormikProps } from "formik";
import * as Yup from "yup";
import ReactPixel from "react-snapchat-pixel";
import { ReactComponent as PawsIcon } from "../../../Images/paws.svg";
import Api from "../../../helpers/apiHelpers";
import { userRole } from "../../../interfaces";
import {useSelector} from "react-redux";
import {logInUser} from "../../../redux/user/user.actions";
import {RootState, useAppDispatch} from "../../../redux/store";
import {colorError} from "../../../Constants/theme";
import {setIsLogOut} from "../../../redux/common/common.reducers";
import {bootIntercom} from "../../../hooks/useIntercom";
import {api_resendConfirmationEmail} from "../../../redux/user/user.api";

declare global {
    interface Window {
        dataLayer:any;
    }
}

interface ILogInResponceBody {
	userId: string,
	email: string,
	expiresAt: string,
	role: userRole,
	token: string,
	admin: boolean,
	name: string,
}

interface LoginFormProps {
    setIsEmailConfirmed: (boolean) => void
}

const LoginForm: React.FC <LoginFormProps> = ({setIsEmailConfirmed}) => {
    const history = useHistory();
    const path = useLocation();
    const dispatch = useAppDispatch();
    const validationSchema = Yup.object().shape({
        email: Yup.string()
            .email("Invalid email")
            .required("Required"),
        password: Yup.string()
            .min(8, "Too Short!")
            .required("Required")
    });
    const [code401Msg, setcode401Msg] = useState((window.location.href.includes("401")) ? "401": "");
    const isLogOut = useSelector((state: RootState) => state?.common.isLogOut);

    useEffect(() => {
        if(isLogOut){
            history.push("/login");
        }
    }, []);

    useEffect(() => {
        if(isLogOut){
            setcode401Msg((window.location.href.includes("401")) ? "401": "");
        }
    }, [isLogOut]);

    return (
        <Formik
            initialValues={{
                email: "",
                password: "",
                isRememberMeChecked: false,
                additionalError: "",
            }}
            onSubmit={
                async (values, {setSubmitting, setStatus, setErrors}) => {
                    dispatch(setIsLogOut(false));

                    const body = {
                        email: values.email.toLowerCase(),
                        password: values.password
                    };
                    try{
                        dispatch(logInUser(body)).then((res) => {
                            Api.token = res.token;
                            Api.isLoggedIn = true;
                            Api.role = res.role;
                            const userIdentification = { user_email: res.email };
                            ReactPixel.init(process.env.REACT_APP_SNAP_ID, userIdentification);
                            ReactPixel.pageView();
                            bootIntercom(res.userId, res.email);
                            if (!res?.isEmailConfirmed) {
                                api_resendConfirmationEmail(values.email.toLowerCase());
                                setIsEmailConfirmed(false);
                            } else {
                                Api.isLoggedIn = true;
                                history.push({
                                    pathname: "/", state: path.state
                                });
                            }
                        });
                        window.dataLayer.push("event", "login", {
                            method: "Email"
						  });
                        setStatus({success: true});
                    } catch (error) {
                        console.error(error);
                        setErrors({additionalError: error.message});
                        setStatus({success: false});
                    }
                    setSubmitting(false);
                }}
            validationSchema={validationSchema}
        >
            {
                ({handleChange, values, isSubmitting, errors}: FormikProps<{email: string, password: string, isRememberMeChecked: boolean, additionalError: string}>) => (
                    <Form>
                        <Box
                            display="flex"
                            flexDirection="column"
                            width="100%"
                            marginBottom="16px"
                        >
                            {
                                (code401Msg && !isLogOut) &&
									<p style={{color: colorError}}>
										Incorrect email or password or not confirmed email. Password is sensitive to uppercased letters.
									</p>
                            }
                            <Field
                                component={TextField}
                                name="email"
                                label="Email Address"
                                variant="outlined"
                                color="secondary"
                                type="email"
                                margin="normal"
                                onChange={handleChange}
                                value={values.email}
                                data-testid="emailInput"
                            />
                            <Field
                                component={TextField}
                                label="Password"
                                name="password"
                                variant="outlined"
                                color="secondary"
                                type="password"
                                margin="normal"
                                onChange={handleChange}
                                value={values.password}
                                data-testid="passwordInput"
                            />
                        </Box>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            marginBottom="25px"
                            flexWrap="wrap"
                        >
                            <Box
                                display="flex"
                                alignItems="center"
                                position="relative"
                                right="9px"
                            />
                            <Typography>
                                <Link to={{pathname: "/forgotPassword", state: path.state}}>Forgot your password?</Link>
                            </Typography>
                        </Box>
                        <Button
                            color="primary"
                            variant="contained"
                            type="submit"
                            disabled={isSubmitting}
                            data-testid="submitButton"
                            fullWidth
                        >
                            <PawsIcon />&nbsp;&nbsp;Login
                        </Button>
                        {
                            errors.additionalError &&
							<Typography className="Mui-error">{errors.additionalError}</Typography>
                        }
                    </Form>
                )
            }
        </Formik>
    );
};

export default LoginForm;
